import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { UiButtonComponent } from '@ui';
import { IntelligenceModels } from '@models/intelligence.model';
import {
  DatasetTableConfig,
  UiDatasetTableComponent,
} from '@ui/lib/ui-kit/ui-dataset-table';
import { IntelligenceResultsService } from '../../../../../services/intelligence-results.service';
import { ChartResultCanvasComponent } from './chart-result-canvas/chart-result-canvas.component';
import { ChartResultExpandedDialogComponent } from './chart-result-expanded-dialog/chart-result-expanded-dialog.component';
import {
  buildChartResultTableRows,
  createChartResultTableConfig,
  TABLE_HEADER_HEIGHT,
  TABLE_ROW_HEIGHT,
} from './chart-result-table.util';
import {
  buildChartDrilldownPrompt,
  isChartBarActionable,
} from './chart-result-drilldown.util';
import {
  formatIdentifier,
  formatTimeRange,
  timeframeTranslationKey,
} from './chart-result.util';
import TableRow = IntelligenceModels.Charts.TableRow;

const EXPANDED_DIALOG_WIDTH = '90vw';
const EXPANDED_DIALOG_MAX_WIDTH = '1100px';

/** Tallest the summary table grows before it scrolls its own rows. */
const TABLE_MAX_HEIGHT = 320;
const DUPLICATE_CLICK_DEBOUNCE_MS = 500;

/**
 * Chart panel for a Cmd+K chart tool result: header, bar chart, and a summary
 * table of the same numbers. Nonzero bars submit a scoped follow-up while the
 * summary table stays read-only.
 *
 * The chart itself lives in `ChartResultCanvasComponent`, shared with the
 * expanded dialog.
 */
@Component({
  selector: 'app-chart-result',
  standalone: true,
  templateUrl: './chart-result.component.html',
  styleUrl: './chart-result.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatTooltip,
    TranslatePipe,
    UiButtonComponent,
    UiDatasetTableComponent,
    ChartResultCanvasComponent,
  ],
})
export class ChartResultComponent {
  private readonly dialog = inject(MatDialog);
  private readonly translateService = inject(TranslateService);
  private readonly intelligenceResultsService = inject(IntelligenceResultsService);
  private lastSelectionKey: string | null = null;
  private lastSelectionAt = 0;

  public readonly response = computed<IntelligenceModels.Charts.Response | null>(() => {
    const result = this.intelligenceResultsService.current();
    if (result?.type !== 'chart') return null;
    return result.data as IntelligenceModels.Charts.Response;
  });

  /** Series that are entirely zero / null add legend clutter with no bars. */
  public readonly series = computed<IntelligenceModels.Charts.SeriesItem[]>(() =>
    (this.response()?.data?.series ?? []).filter((item) =>
      item.values.some((value) => value != null && value > 0),
    ),
  );

  public readonly chartData = computed<IntelligenceModels.Charts.Data | null>(() => {
    const data = this.response()?.data;
    const series = this.series();
    if (!data?.labels.length || !series.length) return null;
    return { ...data, series };
  });

  public readonly title = computed<string>(() =>
    formatIdentifier(this.response()?.plotType ?? ''),
  );

  public readonly timeframeLabel = computed<string>(() => {
    const timeframe = this.response()?.timeframe;
    const key = timeframeTranslationKey(timeframe);
    if (key) return this.translateService.instant(key);
    return timeframe ? formatIdentifier(timeframe) : '';
  });

  public readonly timeRangeLabel = computed<string>(() => {
    const response = this.response();
    return formatTimeRange(response?.timeRange, response?.timeframe);
  });

  public readonly tableConfig = computed<DatasetTableConfig<TableRow>>(() =>
    createChartResultTableConfig(this.series()),
  );

  public readonly tableRows = computed<TableRow[]>(() => {
    const data = this.chartData();
    if (!data) return [];
    return buildChartResultTableRows(data.labels, data.series);
  });

  public readonly isBarActionable: IntelligenceModels.Charts.BarSelectionPredicate =
    (selection) => {
      const response = this.interactiveResponse();
      return response ? isChartBarActionable(response, selection) : false;
    };

  /**
   * The table lays its rows out in a CDK virtual-scroll viewport, which needs a
   * definite height — the panel scrolls as a whole, so there is no flex height
   * to inherit. Grow with the rows up to a cap, then let the table scroll.
   */
  public readonly tableHeight = computed<number>(() =>
    Math.min(
      TABLE_MAX_HEIGHT,
      TABLE_HEADER_HEIGHT + this.tableRows().length * TABLE_ROW_HEIGHT,
    ),
  );

  public openExpanded(): void {
    const response = this.response();
    const data = this.chartData();
    if (!response || !data) return;

    // The dialog draws the same filtered series as the inline chart.
    this.dialog.open<ChartResultExpandedDialogComponent, IntelligenceModels.Charts.Response>(
      ChartResultExpandedDialogComponent,
      {
        panelClass: 'modal-no-padding',
        width: EXPANDED_DIALOG_WIDTH,
        maxWidth: EXPANDED_DIALOG_MAX_WIDTH,
        autoFocus: false,
        data: { ...response, data },
      },
    );
  }

  public onBarSelected(selection: IntelligenceModels.Charts.BarSelection): void {
    const response = this.interactiveResponse();
    const prompt = response
      ? buildChartDrilldownPrompt(response, selection)
      : null;
    if (!prompt || this.isDuplicateSelection(selection)) return;
    this.intelligenceResultsService.requestPrompt(prompt);
  }

  private interactiveResponse(): IntelligenceModels.Charts.Response | null {
    const response = this.response();
    const data = this.chartData();
    return response && data ? { ...response, data } : null;
  }

  private isDuplicateSelection(
    selection: IntelligenceModels.Charts.BarSelection,
  ): boolean {
    const key = `${selection.labelIndex}:${selection.seriesIndex}`;
    const now = Date.now();
    const duplicate =
      key === this.lastSelectionKey &&
      now - this.lastSelectionAt < DUPLICATE_CLICK_DEBOUNCE_MS;
    this.lastSelectionKey = key;
    this.lastSelectionAt = now;
    return duplicate;
  }
}
